import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAppConfig } from "@/api/app-config/useAppConfig";
import { PATHS } from "@/constants/paths";

// NOTE: NSAI-6323 — the guard shared by the two password-flow pages an SSO-only
// deployment closes off, forgot-password and sign-up. It sends the visitor back
// to sign-in and answers whether the page may render at all.
//
// It reads /app-config directly rather than going through
// `usePasswordAuthDisabled`, and the difference is the whole point. That hook
// answers "not disabled" while the request is in flight, deliberately, so SignIn
// does not flash an empty card at every user on every deployment. Here that
// default is the wrong one: these pages exist to be closed off, so treating an
// unanswered request as "allowed" renders the full form — live inputs and a
// working submit button — for a frame or more before the redirect fires. That
// is precisely the password UI this feature removes, and no mocked-route spec
// catches it because a mocked /app-config resolves before the first paint.
//
// So the page waits instead: `AdminGate` does the same thing for the same
// reason, returning null rather than falling through while its flag loads. The
// cost is a blank frame on a cold, direct load of these two pages — and only a
// cold one, since arriving from sign-in finds /app-config already cached.
export const usePasswordFlowGuard = (): boolean => {
  const navigate = useNavigate();
  const { data: appConfig, isLoading, isError } = useAppConfig();

  // NOTE: NSAI-6323 review finding (Greptile) — a failed /app-config settles
  // with no data, so the flag reads "not disabled" and these pages open. That is
  // the deliberate answer, spelled out here rather than left to fall out of the
  // `?? false`, and it is a choice between two wrong answers:
  //
  // - Opening them when the request failed shows a form the server then refuses
  //   by name ("Your organization requires signing in through its SSO
  //   provider"), and only on an SSO-only deployment whose /app-config is
  //   already broken.
  // - Closing them would take password recovery away from every ORDINARY
  //   deployment the moment that one request hiccups, silently and with nothing
  //   on screen to explain it.
  //
  // The first failure is louder, rarer and self-explaining, so it wins. Note
  // also that the server is the enforcement point either way: it refuses these
  // flows for a Require-SSO member whatever the client renders.
  const isConfigResolved = !isLoading || isError;
  const isPasswordAuthDisabled =
    appConfig?.auth.isPasswordAuthDisabled ?? false;

  useEffect(() => {
    if (isConfigResolved && isPasswordAuthDisabled) {
      navigate(PATHS.AUTH.SIGN_IN, { replace: true });
    }
  }, [isConfigResolved, isPasswordAuthDisabled, navigate]);

  return isConfigResolved && !isPasswordAuthDisabled;
};
